import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date';
import {NgbCalendar, NgbDatepicker, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {DateRange} from '../../../models/date-range';
import {NgbDateUtils} from '../../utils/ngb-date.utils';
import {MeasureReportTimeline} from '../../main/measure/measure-setting/measure-setting.component';
import {CommonConstant} from '../../app.constants';

@Component({
  selector: 'achoo-datepicker-range',
  templateUrl: './achoo-datepicker-range.component.html',
  styleUrls: ['./achoo-datepicker-range.component.scss']
})
export class AchooDatepickerRangeComponent implements OnInit {
  @ViewChild('dp') datepicker: NgbDatepicker;
  @Input() selectedDateRange: DateRange;
  @Input() minDate: NgbDate;
  @Input() maxDate: NgbDate;
  @Input() defaultFromDate: NgbDate;
  @Input() defaultToDate: NgbDate;
  @Output() selectionDateRangeEvent: EventEmitter<any> = new EventEmitter();

  hoveredDate: NgbDate;

  fromDate: NgbDate;
  toDate: NgbDate;

  constructor(private calendar: NgbCalendar) {
    this.markDisabled = this.markDisabled.bind(this);
  }

  ngOnInit() {
    if (this.defaultFromDate) {
      this.fromDate = this.defaultFromDate;
    } else {
      this.fromDate = this.calendar.getPrev(this.toDate, 'd', MeasureReportTimeline.A_MONTH);
    }
    if (this.defaultToDate) {
      this.toDate = this.defaultToDate;
    } else {
      this.toDate = this.calendar.getToday();
    }
  }

  resetDateRange() {
    this.toDate = this.calendar.getToday();
    this.fromDate = this.calendar.getPrev(this.toDate, 'd', MeasureReportTimeline.A_MONTH);
    this.datepicker.navigateTo(this.fromDate);
  }

  navigateTo(fromDate: NgbDate) {
    if (!fromDate) {
      return;
    }
    this.datepicker.navigateTo(fromDate);
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && NgbDateUtils.equalOrAfter(date, this.fromDate)) {
      this.toDate = date;
    } else if (this.fromDate && !this.toDate && NgbDateUtils.before(date, this.fromDate)) {
      this.toDate = this.fromDate;
      this.fromDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
      this.markDisabled = this.markDisabled.bind(this);
    }

    if (this.toDate && this.fromDate) {
      this.selectionDateRangeEvent.emit({
        fromDate: this.fromDate,
        toDate: this.toDate
      });
    }
  }

  isHovered(date: NgbDate) {
    if (!date) {
      return false;
    }
    if (this.hoveredDate && NgbDateUtils.after(this.hoveredDate, this.fromDate)) {
      return this.fromDate && !this.toDate
        && NgbDateUtils.after(date, this.fromDate) &&  NgbDateUtils.before(date, this.hoveredDate);
    } else {
      return this.fromDate && !this.toDate && this.hoveredDate
        && NgbDateUtils.after(date, this.hoveredDate) &&  NgbDateUtils.before(date, this.fromDate);
    }
  }

  isInside(date: NgbDate) {
    if (!date) {
      return false;
    }
    return NgbDateUtils.after(date, this.fromDate) && NgbDateUtils.before(date, this.toDate);
  }

  isRange(date: NgbDate) {
    if (!date) {
      return false;
    }
    return NgbDateUtils.equals(date, this.fromDate)
      || NgbDateUtils.equals(date, this.toDate) || NgbDateUtils.equals(date, this.hoveredDate)
      || this.isInside(date) || this.isHovered(date);
  }

  inSelectedRange(date: NgbDate) {
    if (!date) {
      return false;
    }
    return NgbDateUtils.equals(date, this.fromDate) || NgbDateUtils.equals(date, this.toDate)
      || this.isInside(date);
  }

  isMonday(date: NgbDate) {
    return this.calendar.getWeekday(date) === 1;
  }

  isSunday(date: NgbDate) {
    return this.calendar.getWeekday(date) === 7;
  }

  isBorderLeft(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      return false;
    }
    // in case no period is selected
    if (!this.toDate) {
      const firstDate = this.hoveredDate && NgbDateUtils.before(this.hoveredDate, this.fromDate) ? this.hoveredDate : this.fromDate;
      return NgbDateUtils.equals(date, firstDate) || this.isMonday(date) || this.isFirstDateOfMonth(date);
    } else {
      return NgbDateUtils.equals(date, this.fromDate) || ((this.isMonday(date) || this.isFirstDateOfMonth(date)) && this.inSelectedRange(date));
    }
  }

  isBorderRight(date: NgbDate) {
    let lastDate;
    if (this.toDate) {
      lastDate = this.toDate;
    } else if (this.hoveredDate && this.fromDate && NgbDateUtils.after(this.hoveredDate, this.fromDate)) {
      lastDate = this.hoveredDate;
    } else if (this.hoveredDate && this.fromDate && NgbDateUtils.before(this.hoveredDate, this.fromDate)) {
      lastDate = this.fromDate;
    } else {
      lastDate = this.fromDate;
    }
    if (this.toDate && this.fromDate) {
      return NgbDateUtils.equals(date, lastDate) || ((this.isSunday(date) || this.isLastDateOfMonth(date)) && this.inSelectedRange(date));
    } else {
      return NgbDateUtils.equals(date, lastDate) || this.isSunday(date) || this.isLastDateOfMonth(date);
    }
  }

  markDisabled(date: NgbDate, current: {month: number}) {
    const maxDateSupported = this.calendar.getNext(this.fromDate, 'd', CommonConstant.MAX_DAYS_MEASURE);
    const isDisabled = NgbDateUtils.after(date, maxDateSupported);
    return isDisabled;
  }

  private isLastDateOfMonth(date: NgbDate) {
    const lastDayOfMonth = new Date(date.year, date.month, 0);
    return lastDayOfMonth.getDate() === date.day;
  }

  private isFirstDateOfMonth(date: NgbDate) {
    return date.day === 1;
  }

}



// WEBPACK FOOTER //
// ./src/app/core/achoo-datepicker-range/achoo-datepicker-range.component.ts